import './signup/stripeReturn';
import TeamMentionDestination from './mentions/TeamMentionDestination';
import MentionsInbox from './mentions/MentionsInbox';
import MentionDestination from './mentions/MentionDestination';
import HttpsRedirect from 'react-https-redirect';
import { Component, Suspense } from 'react';
import { BrowserRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import axios from 'axios';
import store from 'store';
import * as Sentry from '@sentry/react';
import { ErrorBoundary } from 'react-error-boundary';
import moment from 'moment';
import 'moment/locale/en-au';
import { fetchUser } from '../actions';
import { releaseBrowserPushToken } from '../actions/browserNotifications';
import { getBrandColors } from '../actions/whitelabel';

import ErrorFallback from './ErrorFallback';
import Login from './register/Login';
import { ThemeProvider } from '@/components/ui/theme-provider';
import { hexToHSL } from './utils/hexToHsl';
import { toast } from 'sonner';
import { lazyWithRetry, isChunkLoadError, reloadOnce } from './utils/lazyWithRetry';
import { isStuckStoreError } from '@/utils/stuckReduxStore';

import Notifications from './notifications/Notifications';
import ReminderAlerts from './reminders/ReminderAlerts';
import Layout from './Layout';
import { AppLoading } from './AppLoading';
import WebchatDashboard from './dashboards/WebchatDashboard';
import { isPendingSignup, pendingSignupDestination } from './signup/activation';
import { signupRequestFeedback } from './signup/requestFeedback';

const FacebookLoginAndSelectPage = lazyWithRetry(() => import('./settings/FacebookLoginAndSelectPage'));
const ResellerRegisterPageContainer = lazyWithRetry(() => import('./register/ResellerRegisterPageContainer'));
// The fictional preview has its own entry; production always uses server-backed signup.
const SignupPage = lazyWithRetry(() => import('./signup/LiveSignupPage'));
const SignupOnboardingPage = lazyWithRetry(() => import('./signup/LiveOnboardingPage'));
const WalkthroughsPage = lazyWithRetry(() => import('./walkthroughs/WalkthroughsPage'));
const ForgotPassword = lazyWithRetry(() => import('./register/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('./register/ResetPassword'));
const Policy = lazyWithRetry(() => import('./index/legal/privacyPolicy'));
const TermsOfUse = lazyWithRetry(() => import('./index/legal/termsOfUse'));
const LegalHub = lazyWithRetry(() => import('./legal/LegalHub'));
const ConfirmEmail = lazyWithRetry(() => import('./register/ConfirmEmail'));
const RunnerDashboard = lazyWithRetry(() => import('./dashboards/RunnerDashboard'));
const ReviewDashboard = lazyWithRetry(() => import('./dashboards/ReviewDashboard'));
const SettingsDashboard = lazyWithRetry(() => import('./dashboards/SettingsDashboard'));
const SmartReplyDashboard = lazyWithRetry(() => import('./dashboards/SmartReplyDashboard'));
const MessageAutomationDashboard = lazyWithRetry(() => import('./dashboards/MessageAutomationDashboard'));
const LeaderboardDashboard = lazyWithRetry(() => import('./dashboards/LeaderboardDashboard'));
const ReminderDashboard = lazyWithRetry(() => import('./dashboards/ReminderDashboard'));
const ReminderSequencesPage = lazyWithRetry(() => import('./reminder-sequences/ReminderSequencesPage'));
const CompetitorDashboard = lazyWithRetry(() => import('./dashboards/CompetitorDashboard'));
const FormsDashboard = lazyWithRetry(() => import('./dashboards/FormsDashboard'));
const MessengerAnalyticsDashboard = lazyWithRetry(() => import('./dashboards/MessengerAnalyticsDashboard'));
const PaymentsDashboard = lazyWithRetry(() => import('./dashboards/PaymentsDashboard'));
const ZapierDashboard = lazyWithRetry(() => import('./dashboards/ZapierDashboard'));
const ApiDashboard = lazyWithRetry(() => import('./dashboards/ApiDashboard'));
const AutomationDashboard = lazyWithRetry(() => import('./dashboards/AutomationDashboard'));
const TemplatesDashboard = lazyWithRetry(() => import('./dashboards/TemplatesDashboard'));
const Dashboard = lazyWithRetry(() => import('./campaign-creator/Dashboard'));
const MessageSequenceDashboard = lazyWithRetry(() => import('./message-sequence/Dashboard'));
const PipelineDashboard = lazyWithRetry(() => import('./pipeline/Dashboard'));
const ActivityFeed = lazyWithRetry(() => import('./activity-feed'));
const PaymentDetailsPage = lazyWithRetry(() => import('./payment/PaymentDetailsPage'));

const CustomerTable = lazyWithRetry(() => import('./runner/customer/table/CustomerTable').then(m => ({ default: m.CustomerTable })));
const CustomerDetails = lazyWithRetry(() => import('./runner/customer/CustomerDetails').then(m => ({ default: m.CustomerDetails })));
const InvoicesTable = lazyWithRetry(() => import('./runner/tables/InvoicesTable').then(m => ({ default: m.InvoicesTable })));
const JobsTable = lazyWithRetry(() => import('./runner/tables/JobsTable').then(m => ({ default: m.JobsTable })));
const QuotesTable = lazyWithRetry(() => import('./runner/tables/QuotesTable').then(m => ({ default: m.QuotesTable })));
const Service = lazyWithRetry(() => import('./runner/create/Service').then(m => ({ default: m.Service })));
const Create = lazyWithRetry(() => import('./runner/Create').then(m => ({ default: m.Create })));
const Edit = lazyWithRetry(() => import('./runner/Edit').then(m => ({ default: m.Edit })));
const CalendarOverview = lazyWithRetry(() => import('./runner/CalendarOverview').then(m => ({ default: m.CalendarOverview })));
const CalendarIntegrationsPage = lazyWithRetry(() => import('./runner/calendar/integrations/CalendarIntegrationsPage'));
const Today = lazyWithRetry(() => import('./runner/home/today/Today').then(m => ({ default: m.Today })));
const AllWorkDashboard = lazyWithRetry(() => import('./runner/home/AllWork/AllWorkDashboard').then(m => ({ default: m.AllWorkDashboard })));
const CallHistory = lazyWithRetry(() => import('./voice/CallHistory').then(m => ({ default: m.CallHistory })));

const AiEmployeeDashboard = lazyWithRetry(() => import('./ai-employee/index'));
const TrainingChat = lazyWithRetry(() => import('./ai-employee/components/training-chat').then(m => ({ default: m.TrainingChat })));
const KnowledgeProfile = lazyWithRetry(() => import('./ai-employee/components/knowledge-profile').then(m => ({ default: m.KnowledgeProfile })));
const BusinessOverview = lazyWithRetry(() => import('./ai-employee/components/business-overview').then(m => ({ default: m.BusinessOverview })));
const DocumentSources = lazyWithRetry(() => import('./ai-employee/components/knowledge-sources').then(m => ({ default: m.DocumentSources })));
const ProductsPage = lazyWithRetry(() => import('./ai-employee/components/products/products-page').then(m => ({ default: m.ProductsPage })));
const WebsiteSources = lazyWithRetry(() => import('./ai-employee/components/website-sources').then(m => ({ default: m.WebsiteSources })));
const TestChat = lazyWithRetry(() => import('./ai-employee/components/test-chat').then(m => ({ default: m.TestChat })));
const FaqManager = lazyWithRetry(() => import('./ai-employee/components/faq-manager').then(m => ({ default: m.FaqManager })));
const GoogleCalendarPage = lazyWithRetry(() => import('./ai-employee/components/integrations/google-calendar-page').then(m => ({ default: m.GoogleCalendarPage })));
const AiSettings = lazyWithRetry(() => import('./ai-employee/components/ai-settings').then(m => ({ default: m.AiSettings })));
const AiPersonality = lazyWithRetry(() => import('./ai-employee/components/personality').then(m => ({ default: m.AiPersonality })));
const TopicRulesLayout = lazyWithRetry(() => import('./ai-employee/components/topic-rules/topic-rules-layout').then(m => ({ default: m.TopicRulesLayout })));
const TopicRulesPage = lazyWithRetry(() => import('./ai-employee/components/topic-rules/topic-rules-page').then(m => ({ default: m.TopicRulesPage })));
const TopicRuleWizard = lazyWithRetry(() => import('./ai-employee/components/topic-rules/topic-rule-wizard').then(m => ({ default: m.TopicRuleWizard })));
const TopicRuleEditPage = lazyWithRetry(() => import('./ai-employee/components/topic-rules/topic-rule-edit-page').then(m => ({ default: m.TopicRuleEditPage })));
const GoalsLayout = lazyWithRetry(() => import('./ai-employee/components/goals/goals-layout').then(m => ({ default: m.GoalsLayout })));
const GoalsList = lazyWithRetry(() => import('./ai-employee/components/goals/v2/goals-list').then(m => ({ default: m.GoalsListV2 })));
const NewGoalPage = lazyWithRetry(() => import('./ai-employee/components/goals/v2/new-goal-page').then(m => ({ default: m.NewGoalPage })));
const GoalEditor = lazyWithRetry(() => import('./ai-employee/components/goals/v2/goal-editor').then(m => ({ default: m.GoalEditorV2 })));
const AiOverview = lazyWithRetry(() => import('./ai-employee/components/overview').then(m => ({ default: m.AiOverview })));
const EscalationQueue = lazyWithRetry(() => import('./ai-employee/components/escalation-queue'));
const UsageDashboard = lazyWithRetry(() => import('./ai-employee/components/usage-dashboard').then(m => ({ default: m.UsageDashboard })));
const SetupWizard = lazyWithRetry(() => import('./ai-employee/setup/setup-wizard').then(m => ({ default: m.SetupWizard })));

Sentry.init({
	release: 'chekkit-react@1.0.0',
	dsn: 'https://d37f0509d3e547439d21eb0cd8eeb112@o357142.ingest.us.sentry.io/2870435',
	integrations: [Sentry.browserTracingIntegration()],
	denyUrls: [
		/googletagmanager\.com/,
		/google-analytics\.com/,
		/googleadservices\.com/,
		/js\.stripe\.com/,
		/maps\.googleapis\.com/,
	],
	ignoreErrors: [
		/play\(\) failed because the user didn't interact with the document first/,
	],
	beforeSend(event) {
		const value = event.exception?.values?.[0]?.value || '';
		if (value.includes('CustomEvent') && value.includes('unhandledrejection')) {
			return null;
		}
		if (isChunkLoadError({ message: value })) {
			return null;
		}
		if (value.includes('play()') && value.includes("didn't interact")) {
			return null;
		}
		return event;
	},
});

class App extends Component {
	state = { show: false, loading: true };

	constructor(props) {
		super(props);
		this.v4muiTheme = {};
	}

	componentDidMount = async () => {
		this.setUpAxios();
		await this.setUpTheme();

		if (!this.props.auth) {
			await this.fetchUser();
		}

		this.setUpMoment();
		this.configureSentry();

		this.setState({ loading: false });
	};

	componentDidUpdate = (prevProps) => {
		if (prevProps.auth?.timezone !== this.props.auth?.timezone) {
			this.setUpMoment();
		}
	};

	componentWillUnmount = () => {
		// Clean up the theme observer
		if (this.themeObserver) {
			this.themeObserver.disconnect();
		}
	};

	setUpMoment = () => {
		if (
			this.props.auth &&
			this.props.auth.timezone &&
			this.props.auth.timezone.includes('Australia')
		) {
			moment.locale('en-au');

			moment.updateLocale('en-au', {
				longDateFormat: {
					L: 'DD/MM/YYYY',
					LL: 'D MMMM YYYY',
					ll: 'D MMM YYYY',
					LTS: 'D MMM',
					LLL: 'DD MMMM YYYY, h:mm a',
					lll: 'DD MMMM, h:mm a',
					l: 'DD-MM-YYYY',
					LLLL: 'ddd, DD MMMM YYYY',
					llll: 'DD MMM YYYY h:mm A',
				},
			});
		} else {
			moment.updateLocale('en', {
				longDateFormat: {
					LTS: 'MMM D',
					LLL: 'MMMM DD YYYY, h:mm a',
					lll: 'MMMM DD, h:mm a',
					l: 'MM-DD-YYYY',
					LLLL: 'ddd, MMMM DD, YYYY',
					llll: 'MMM DD YYYY h:mm A',
				},
			});
		}
	};

	updateBrandColor = (color) => {
		// Apply the theme-appropriate color from getBrandColors
		document.documentElement.style.setProperty(
			'--primary',
			hexToHSL(color),
		);
		document.documentElement.style.setProperty('--ring', hexToHSL(color));
	};

	// address will be in the form: 'street, city, state, zipCode, country'
	// the country will be US, CA, or AU
	// we want to return the country as United States, Canada, or Australia
	getCountryFromAddress = (address) => {
		if (!address) {
			return undefined;
		}

		const addressArray = address?.split(',');

		if (addressArray.length < 5) {
			return undefined;
		}

		const country = addressArray[4].trim();

		if (country === 'US') {
			return 'United States';
		} else if (country === 'CA') {
			return 'Canada';
		} else if (country === 'AU') {
			return 'Australia';
		}

		return undefined;
	};

	// we need to include jwt in every request
	setUpAxios = () => {
		const token = store.get('jwt');

		if (token) {
			// set default authorization headers so that the JWT
			// is sent with every request.
			const AuthorizationHeader = 'Authorization';
			axios.defaults.headers.common[AuthorizationHeader] = `JWT ${token}`;
			axios.defaults.withCredentials = true;
		}

		// Set a sane default timeout so requests fail fast
		axios.defaults.timeout = 15000;

		// Avoid installing interceptors multiple times in dev/hot-reload
		if (!axios.__chekkitInterceptorsInstalled) {
			axios.__chekkitInterceptorsInstalled = true;

			// NOTES:
			// use axios.get('/api/some-endpoint', { _suppressErrorToast: true }) if you want to suppress error
			// override time out by axios.post('/api/heavy-task', data, { timeout: 60000 })
			axios.interceptors.response.use(
				(response) => response,
				(error) => {
					// Ignore cancellations
					if (axios.isCancel && axios.isCancel(error)) {
						return Promise.reject(error);
					}

					const requestUrl =
						(error && error.config && error.config.url) || '';

					// Handle 401 Unauthorized - session expired or invalid token
					// Only redirect for our own API calls, not third-party APIs
					if (
						error &&
						error.response &&
						error.response.status === 401 &&
						typeof requestUrl === 'string' &&
						requestUrl.startsWith('/api/')
					) {
						// Only redirect if not already on login page (avoid redirect loop)
						if (!window.location.pathname.startsWith('/login')) {
							store.remove('jwt');
							// a signed-out browser must stop getting desktop notifications
							releaseBrowserPushToken();
							window.location.href = '/login';
						}
						// Suppress toast for 401s - user is being redirected to login
						return Promise.reject(error);
					}

					// Allow per-request opt-out
					if (
						error &&
						error.config &&
						error.config._suppressErrorToast
					) {
						return Promise.reject(error);
					}

					// Capability restrictions are normal while a new workspace is being
					// prepared. Avoid noisy red errors on background page loads; show
					// one clear notice for a deliberate restricted action instead.
					const signupFeedback = signupRequestFeedback(error);
					if (signupFeedback) {
						if (signupFeedback === 'notice') {
							toast.info('Texting setup is not finished', {
								id: 'signup-texting-setup-required',
								description: typeof error.response?.data?.message === 'string'
									? error.response.data.message
									: 'You can explore your dashboard while texting setup is completed.',
							});
						}
						return Promise.reject(error);
					}

					// Intentional control-flow response: a 412 AGREEMENT_REQUIRED
					// signals the caller to prompt in-app agreement acceptance
					// (HIPAA BAA, payments, voice, AI). It's not a failure, so let
					// the caller handle it without a scary error toast.
					if (
						error &&
						error.response &&
						error.response.status === 412 &&
						error.response.data &&
						error.response.data.code === 'AGREEMENT_REQUIRED'
					) {
						return Promise.reject(error);
					}

					// Silence very noisy endpoints and auth-related endpoints
					if (typeof requestUrl === 'string') {
						const silentSubstrings = [
							'/api/webchat/typing',
							'customer-invoices',
							'/api/current_user',
							// Both upload screens show their own import error.
							'/api/csv/upload-csv-for-messaging',
						];
						if (
							silentSubstrings.some((s) => requestUrl.includes(s))
						) {
							return Promise.reject(error);
						}
					}

					// Build a friendly error message
					let description = 'An unexpected error occurred.';
					let title = 'Request failed';

					if (error && error.response) {
						const { status, data } = error.response;
						const serverMessage =
							typeof data === 'string'
								? data
								: (data && (data.message || data.error)) ||
									null;
						description =
							serverMessage ||
							`Server responded with status ${status}.`;
					} else if (error && error.request) {
						title = 'Network error';
						description =
							'No response received. Please check your connection and try again.';
					} else if (error && error.message) {
						description = error.message;
					}

					// Show a toast for visibility
					try {
						toast.error(title, {
							description,
							duration: 5000,
						});
					} catch {
						// noop if toast system isn't ready yet
					}

					return Promise.reject(error);
				},
			);
		}
	};

	setUpTheme = async () => {
		const { primaryColor } = await this.props.getBrandColors();
		this.updateBrandColor(primaryColor);

		// Store the primary color for theme switching
		this.primaryColor = primaryColor;

		// Listen for theme changes to re-apply brand color logic
		this.setupThemeChangeListener();
	};

	setupThemeChangeListener = () => {
		// Create a mutation observer to watch for theme class changes
		const observer = new MutationObserver(async () => {
			// Re-fetch brand colors when theme changes to get theme-appropriate colors
			const { primaryColor } = await this.props.getBrandColors();
			this.updateBrandColor(primaryColor);
			this.primaryColor = primaryColor;
		});

		// Watch for changes to the class attribute of the document element
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['class'],
		});

		// Store the observer for cleanup
		this.themeObserver = observer;
	};

	configureSentry = () => {
		const { auth } = this.props;

		if (import.meta.env.MODE === 'production') {
			Sentry.setUser({
				email: auth && auth.user && auth.user.username,
			});
		}
	};

	fetchUser = async () => {
		const done = await this.props.fetchUser();
		if (done) {
			this.setState({ show: true });
		}
	};

	logError = (error) => {
		// Stale chunk after a deploy: reload once to fetch the new asset
		// manifest instead of showing the error screen.
		if (isChunkLoadError(error) && reloadOnce()) {
			return;
		}

		// The Redux store is stuck mid-dispatch and every render will throw;
		// a reload is the only way back (see utils/stuckReduxStore).
		if (isStuckStoreError(error) && reloadOnce()) {
			return;
		}

		Sentry.captureException(error);
		console.error('Error caught by ErrorBoundary:', error);
	};

	loggedIn = () => this.props.auth;

	render() {
		if (this.state.loading) {
			return (
				<ThemeProvider defaultTheme="light" storageKey="ui-theme">
					<ErrorBoundary
						FallbackComponent={ErrorFallback}
						onError={this.logError}
					>
						<AppLoading />
					</ErrorBoundary>
				</ThemeProvider>
			);
		}

		return (
			<ThemeProvider defaultTheme="light" storageKey="ui-theme">
				<div className="font-sans bg-background">
					<Notifications />
					<ErrorBoundary
						FallbackComponent={ErrorFallback}
						onError={this.logError}
					>
						<HttpsRedirect>
							<BrowserRouter>
								{!isPendingSignup(this.props.auth) && <ReminderAlerts />}
								<Suspense fallback={<AppLoading />}>
									<Routes>
										<Route path="/signup/*" element={<SignupPage />} />
										<Route
											path="/login"
											element={<Login />}
										/>

										<Route
											path="/start"
											element={
												<ResellerRegisterPageContainer />
											}
										/>

										<Route
											path="/confirm-email/:token"
											element={<ConfirmEmail />}
										/>

										<Route
											path="/facebook-login"
											element={
												<FacebookLoginAndSelectPage />
											}
										/>

										<Route
											path="/forgot-password"
											element={<ForgotPassword />}
										/>

										<Route
											path="/reset-password/:token"
											element={<ResetPassword />}
										/>

										<Route
											path="/terms-of-use"
											element={<TermsOfUse />}
										/>

										<Route
											path="/privacy-policy"
											element={<Policy />}
										/>

										<Route
											path="/account-not-active"
											element={<Login />}
										/>

										<Route
											path="/"
											element={
												<RequireAuth>
													<Layout />
												</RequireAuth>
											}
										>
											<Route
												index
												element={<WebchatDashboard />}
											/>
											<Route path="onboarding" element={<SignupOnboardingPage />} />
											<Route path="walkthroughs" element={<WalkthroughsPage />} />

											<Route
												path={'/work'}
												element={<RunnerDashboard />}
											>
												<Route
													index
													path={'today'}
													element={<Today />}
												/>

												<Route
													path={'everything'}
													element={
														<AllWorkDashboard />
													}
												/>

												<Route
													path={'invoices'}
													element={<InvoicesTable />}
												/>
												<Route
													path={'jobs'}
													element={<JobsTable />}
												/>

												<Route
													path={'quotes'}
													element={<QuotesTable />}
												/>

												<Route
													path={'services'}
													element={<Service />}
												/>

												<Route
													path={'calendar'}
													element={
														<CalendarOverview />
													}
												/>
												<Route
													path={
														'calendar/integrations'
													}
													element={
														<CalendarIntegrationsPage />
													}
												/>

												<Route
													path={'create'}
													element={<Create />}
												/>

												<Route
													path={
														'create/:subdashboard'
													}
													element={<Create />}
												/>

												<Route
													path={
														'edit/:subdashboard/:_id'
													}
													element={<Edit />}
												/>

												<Route
													path={
														'view/:subdashboard/:_id'
													}
													element={<Edit />}
												/>

												<Route
													path={'*'}
													element={<Today />}
												/>
											</Route>

											<Route
												path={'pipeline'}
												element={<PipelineDashboard />}
											>
												<Route
													path="item/:itemNumber"
													element={
														<PipelineDashboard />
													}
												/>
											</Route>

											<Route
												path={'activity-feed'}
												element={<ActivityFeed />}
											/>

											<Route
												path={'/reviews'}
												element={<ReviewDashboard />}
											>
												<Route
													path="leaderboard"
													element={
														<LeaderboardDashboard />
													}
												/>
												<Route
													path="competitor"
													element={
														<CompetitorDashboard />
													}
												/>
											</Route>
											<Route
												path={'/compliance-settings'}
												element={<SettingsDashboard />}
											/>
											<Route
												path={'/business-settings'}
												element={<SettingsDashboard />}
											/>
											{/* Webchat Routes */}
                      <Route path="/team-mentions" element={<MentionsInbox key="team" team />} />
                      <Route path="/team-mentions/:id" element={<TeamMentionDestination />} />
                      <Route path="/mentions" element={<MentionsInbox />} />
                      <Route path="/mentions/:id" element={<MentionDestination />} />
											{/** "ALL" || "UNASSIGNED" || "MY_MESSAGES" || "CONTACTS" || "USER_" || "DEPARTMENT_" || "LOCATION_" || "NEW_CHAT" || "FOLDER_ */}

											<Route
												path="/conversation/:_customerConversation"
												element={
													<WebchatDashboard
														assignedType={'ALL'}
													/>
												}
											/>

											<Route
												path="/sequences"
												element={
													<MessageSequenceDashboard />
												}
											/>

											<Route
												path="/inbox"
												element={
													<WebchatDashboard
														assignedType={'ALL'}
													/>
												}
											>
												<Route
													path="conversation/:_customerConversation"
													element={
														<WebchatDashboard assignedType="ALL" />
													}
												/>
												<Route
													path="new"
													element={
														<WebchatDashboard assignedType="ALL" />
													}
												/>
											</Route>

											<Route
												path="/tags/:_tag"
												element={
													<WebchatDashboard assignedType="TAG_" />
												}
											>
												<Route
													path="conversation/:_customerConversation"
													element={
														<WebchatDashboard assignedType="TAG_" />
													}
												/>
											</Route>
											<Route
												path="/locations/:_location"
												element={
													<WebchatDashboard assignedType="LOCATION_" />
												}
											>
												<Route
													path="conversation/:_customerConversation"
													element={
														<WebchatDashboard assignedType="LOCATION_" />
													}
												/>
											</Route>
											<Route
												path="/unassigned"
												element={
													<WebchatDashboard assignedType="UNASSIGNED" />
												}
											/>
											<Route
												path="/unassigned/conversation/:_customerConversation"
												element={
													<WebchatDashboard assignedType="UNASSIGNED" />
												}
											/>
											<Route
												path="/my-messages"
												element={
													<WebchatDashboard assignedType="MY_MESSAGES" />
												}
											/>
											<Route
												path="/my-messages/conversation/:_customerConversation"
												element={
													<WebchatDashboard assignedType="MY_MESSAGES" />
												}
											/>
											<Route
												path="/folders/:_folder"
												element={
													<WebchatDashboard assignedType="FOLDER_" />
												}
											>
												<Route
													path="conversation/:_customerConversation"
													element={
														<WebchatDashboard assignedType="FOLDER_" />
													}
												/>
											</Route>

											<Route
												path="/users/:_user"
												element={
													<WebchatDashboard assignedType="USER_" />
												}
											>
												<Route
													path="conversation/:_customerConversation"
													element={
														<WebchatDashboard assignedType="USER_" />
													}
												/>
											</Route>
											<Route
												path="/departments/:_department"
												element={
													<WebchatDashboard assignedType="DEPARTMENT_" />
												}
											>
												<Route
													path="conversation/:_customerConversation"
													element={
														<WebchatDashboard assignedType="DEPARTMENT_" />
													}
												/>
											</Route>

											<Route
												path="/reminders"
												element={<ReminderDashboard />}
											/>
											<Route
												path="/reminders/sequences"
												element={<ReminderSequencesPage />}
											/>

											<Route
												path="/call-history"
												element={<CallHistory />}
											/>

											<Route
												path="/templates"
												element={<TemplatesDashboard />}
											/>

											<Route
												path="/contacts"
												element={<CustomerTable />}
											/>

											<Route
												path="/ai/start"
												element={<SetupWizard />}
											/>

											<Route
												path="/ai"
												element={
													<AiEmployeeDashboard />
												}
											>
												<Route
													index
													element={
														<Navigate
															to="overview"
															replace
														/>
													}
												/>
												<Route
													path="overview"
													element={<AiOverview />}
												/>
												<Route
													path="train"
													element={<TrainingChat />}
												/>
												<Route
													path="knowledge"
													element={<KnowledgeProfile />}
												/>
												<Route
													path="business-overview"
													element={<BusinessOverview />}
												/>
												<Route
													path="documents"
													element={
														<DocumentSources />
													}
												/>
												<Route
													path="products"
													element={<ProductsPage />}
												/>
												<Route
													path="websites"
													element={<WebsiteSources />}
												/>
												<Route
													path="test"
													element={<TestChat />}
												/>
												<Route
													path="faqs"
													element={<FaqManager />}
												/>
												<Route
													path="escalations"
													element={
														<EscalationQueue />
													}
												/>
												<Route
													path="usage"
													element={<UsageDashboard />}
												/>
												<Route
													path="replies"
													element={<AiSettings />}
												/>
												<Route
													path="integrations"
													element={
														<Navigate
															to="google-calendar"
															replace
														/>
													}
												/>
												<Route
													path="integrations/google-calendar"
													element={<GoogleCalendarPage />}
												/>
												<Route
													path="personality"
													element={<AiPersonality />}
												/>
												<Route
													path="topic-rules"
													element={<TopicRulesLayout />}
												>
													<Route
														index
														element={<TopicRulesPage />}
													/>
													<Route
														path="new"
														element={<TopicRuleWizard />}
													/>
													<Route
														path=":ruleId"
														element={<TopicRuleEditPage />}
													/>
												</Route>
												<Route
													path="goals"
													element={<GoalsLayout />}
												>
													<Route
														index
														element={<GoalsList />}
													/>
													<Route
														path="new"
														element={<NewGoalPage />}
													/>
													<Route
														path=":goalId"
														element={<GoalEditor />}
													/>
												</Route>
											</Route>

											<Route
												path="/customers"
												element={<CustomerTable />}
											/>

											<Route
												path={'customers/:_id'}
												element={<CustomerDetails />}
											/>

											<Route
												path="/campaigns"
												element={<Dashboard />}
											/>
											<Route
												path={'/automations'}
												element={
													<AutomationDashboard />
												}
											>
												<Route
													path="smart-replies"
													element={
														<SmartReplyDashboard />
													}
												/>

												<Route
													path="message-automations"
													element={
														<MessageAutomationDashboard />
													}
												/>

												<Route
													path="zapier"
													element={
														<ZapierDashboard />
													}
												/>

												<Route
													path="api"
													element={<ApiDashboard />}
												/>
											</Route>

											<Route
												path="/settings/ai"
												element={
													<Navigate
														to="/ai/replies"
														replace
													/>
												}
											/>

											<Route
												path="/settings"
												element={<SettingsDashboard />}
											>
												<Route
													path="business-details"
													element={
														<SettingsDashboard />
													}
												/>
												<Route
													path="brand"
													element={
														<SettingsDashboard />
													}
												/>
												<Route
													path="spam-words"
													element={
														<SettingsDashboard />
													}
												/>
												<Route
													path="business"
													element={
														<SettingsDashboard />
													}
												/>
												<Route
													path="team"
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path="templates"
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path="notifications"
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path="departments"
													element={
														<SettingsDashboard />
													}
												/>
												<Route
													path="reviews"
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path="google"
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path="facebook"
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path="inbound-webhook"
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path="messaging-webhook"
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path="phone"
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path="auto-reply-media"
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path="textchat"
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path="voice"
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path="pay"
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path={'billing'}
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path={'compliance'}
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path={'website-form'}
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path={'opt-in-form'}
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path={
														'products-and-services'
													}
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path={'tax-lines'}
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path={'workflows'}
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path={'event-log'}
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path="crm-tools"
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path={'tag-manager'}
													element={
														<SettingsDashboard />
													}
												/>

												<Route
													path="privacy"
													element={
														<SettingsDashboard />
													}
												/>
											</Route>
											<Route
												path="/forms"
												element={<FormsDashboard />}
											/>

											<Route
												path="/feedback"
												element={<FormsDashboard />}
											/>

											<Route
												path="/messenger-analytics"
												element={
													<MessengerAnalyticsDashboard />
												}
											/>
											<Route
												path="/payments"
												element={<PaymentsDashboard />}
											/>
											<Route
												path="/legal"
												element={<LegalHub />}
											/>
											<Route
												path="/legal/:slug"
												element={<LegalHub />}
											/>
											<Route
												path="/payments/details/:type/:id"
												element={
													<PaymentDetailsPage
														onCustomerClick={(
															invoice,
														) => {
															if (
																invoice
																	?._customer
																	?.id
															) {
																// Navigate to payments dashboard with customer filter
																window.location.href = `/payments?customer=${
																	invoice
																		._customer
																		.id
																}&customerName=${encodeURIComponent(
																	invoice
																		._customer
																		.name ||
																		'',
																)}`;
															} else {
																console.log(
																	'No customer ID found',
																);
															}
														}}
													/>
												}
											/>

											<Route
												path="/scheduled-messages"
												element={<Dashboard />}
											/>

											{/** /conversation/groups/_teamConversation?_location&_teamMessage&isDirectMessage */}
											{/* http://localhost:3000/conversation/groups/678532fd68b6c664f7f72371?_location=63e40f0919461c56b70f64dd&_teamMessage=67a3e8003084c70a2ca4a684&isDirectMessage=false */}
											<Route
												path="/groupchat"
												element={
													<Navigate
														to="/groupchat/groups"
														replace
													/>
												}
											/>

											<Route
												path="/conversation/groups/:_teamConversation"
												element={<WebchatDashboard />}
											/>

											<Route
												path="/groupchat/groups"
												element={<WebchatDashboard />}
											>
												<Route
													path="conversation/groups/:_teamConversation"
													element={
														<WebchatDashboard />
													}
												/>
											</Route>
											<Route
												path="/groupchat/dms"
												element={<WebchatDashboard />}
											>
												<Route
													path="conversation/groups/:_teamConversation"
													element={
														<WebchatDashboard />
													}
												/>
											</Route>
										</Route>

										<Route
											path="*"
											element={<RequireAuth />}
										/>
									</Routes>
								</Suspense>
							</BrowserRouter>
						</HttpsRedirect>
					</ErrorBoundary>
				</div>
			</ThemeProvider>
		);
	}
}

const RequireAuth = ({ children }) => {
	const auth = useSelector((state) => state.auth);
	const location = useLocation();

	if (!auth) {
    try { if (/^\/(?:team-mentions|mentions)(?:\/[^/]+)?$/.test(window.location.pathname)) sessionStorage.setItem('pending-mention-path', window.location.pathname); } catch { /* storage may be disabled */ }
		return <Navigate to="/login" />;
	}
	const pendingDestination = isPendingSignup(auth) && pendingSignupDestination(location.pathname, auth);
	if (pendingDestination) return <Navigate to={pendingDestination} replace />;

	if (!children) {
		return <Navigate to="/inbox" />;
	}

	return children;
};

function mapStateToProps(state) {
	return {
		auth: state.auth,
		notifications: state.notifications,
	};
}

export default Sentry.withProfiler(
	connect(mapStateToProps, { fetchUser, getBrandColors })(App),
);
